import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Terminal } from "lucide-react";
import { cn } from "@/lib/utils";

const MENU_WIDTH = 320;
const MENU_MAX_HEIGHT = 280;
const VIEWPORT_PAD = 8;
const CARET_GAP = 6;

/**
 * Floating autocomplete for leading-/ Circatron slash commands.
 */
export default function SlashCommandSuggestionList({
  items = [],
  selectedIndex = 0,
  onSelect,
  onHover,
  anchorRect,
  loading = false,
  emptyText = "No commands",
}) {
  const listRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    if (!anchorRect || typeof window === "undefined") {
      setCoords(null);
      return;
    }

    const measuredHeight = Math.min(
      MENU_MAX_HEIGHT,
      Math.max(listRef.current?.scrollHeight || 0, 120)
    );
    const spaceBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_PAD;
    const spaceAbove = anchorRect.top - VIEWPORT_PAD;
    const openAbove =
      spaceBelow < Math.min(measuredHeight, MENU_MAX_HEIGHT) &&
      spaceAbove > spaceBelow;

    const maxHeight = Math.max(
      96,
      Math.min(
        MENU_MAX_HEIGHT,
        openAbove ? spaceAbove - CARET_GAP : spaceBelow - CARET_GAP
      )
    );

    const height = Math.min(
      maxHeight,
      listRef.current?.offsetHeight || measuredHeight
    );

    let left = anchorRect.left;
    left = Math.min(left, window.innerWidth - MENU_WIDTH - VIEWPORT_PAD);
    left = Math.max(VIEWPORT_PAD, left);

    const top = openAbove
      ? Math.max(VIEWPORT_PAD, anchorRect.top - CARET_GAP - height)
      : anchorRect.bottom + CARET_GAP;

    setCoords({ left, top, maxHeight, openAbove });
  }, [anchorRect, items, loading, emptyText]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-selected="true"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex, coords]);

  if (!anchorRect) return null;

  const style = {
    position: "fixed",
    left: coords?.left ?? Math.max(VIEWPORT_PAD, anchorRect.left),
    top: coords?.top ?? anchorRect.bottom + CARET_GAP,
    zIndex: 200,
    width: MENU_WIDTH,
    maxHeight: coords?.maxHeight ?? MENU_MAX_HEIGHT,
  };

  return createPortal(
    <div
      ref={listRef}
      role="listbox"
      aria-label="Slash commands"
      className="overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
      style={style}
    >
      {loading ? (
        <div className="px-3 py-2 text-xs text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="px-3 py-2 text-xs text-muted-foreground">{emptyText}</div>
      ) : (
        items.map((item, index) => {
          const selected = index === selectedIndex;
          return (
            <button
              key={`${item.installation_id}-${item.command}`}
              type="button"
              role="option"
              aria-selected={selected}
              data-selected={selected ? "true" : undefined}
              className={cn(
                "flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent",
                selected && "bg-accent"
              )}
              onMouseEnter={() => onHover?.(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect?.(item);
              }}
            >
              <span className="mt-0.5 shrink-0 text-muted-foreground">
                <Terminal className="h-3.5 w-3.5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-baseline gap-2 min-w-0">
                  <span className="font-medium truncate">/{item.command}</span>
                  <span className="text-xs text-muted-foreground truncate">
                    {item.bot_name}
                  </span>
                </span>
                {item.description ? (
                  <span className="block truncate text-xs text-muted-foreground">
                    {item.description}
                  </span>
                ) : null}
                {item.example ? (
                  <span className="block truncate text-[11px] text-muted-foreground/80 font-mono">
                    {item.example}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })
      )}
    </div>,
    document.body
  );
}
